import { useCallback, useEffect, useRef, useState } from "react";
import {
  SvgStatusError,
  SvgStatusSuccess,
} from "@itwin/itwinui-icons-react";
import {
  Button,
  Flex,
  ProgressRadial,
  Surface,
  Text,
} from "@itwin/itwinui-react";
import {
  IProvisionableConsumer,
  ISubscriptionDraft,
  ProvisionStatus,
} from "../../interfaces/IProvisioning";
import { IProducer } from "../../interfaces/IProducer";
import { useCreateConsumer } from "../../hooks/useCreateConsumer/useCreateConsumer";
import { useCreateSubscription } from "../../hooks/useCreateSubscription/useCreateSubscription";
import { useGetConsumer } from "../../hooks/useGetConsumer/useGetConsumer";
import { useGetSubscription } from "../../hooks/useGetSubscription/useGetSubscription";
import { ISubscriptionCreateRequest } from "../../interfaces/ISubscriptionCreateRequest";

export interface ProvisioningViewProps {
  consumer: IProvisionableConsumer;
  subscriptions: ISubscriptionDraft[];
  producers: IProducer[];
  onComplete: (consumerId: string) => void;
  onConsumerCreated?: (consumerId: string) => void;
  onBack?: () => void;
}

interface ProvisioningItem {
  type: "consumer" | "subscription";
  name: string;
  status: ProvisionStatus;
  errorMessage?: string;
  /** For subscriptions, the draft ID */
  draftId?: string;
}

export const ProvisioningView = (props: ProvisioningViewProps) => {
  const { consumer, subscriptions, producers, onComplete, onConsumerCreated, onBack } = props;

  const [items, setItems] = useState<ProvisioningItem[]>(() => {
    // Initialize with consumer first, then subscriptions
    const consumerItem: ProvisioningItem = {
      type: "consumer",
      name: consumer.description || "New Consumer",
      status: "pending",
    };

    const subscriptionItems: ProvisioningItem[] = subscriptions.map((sub) => {
      const producer = producers.find((p) => p.id === sub.producerId);
      return {
        type: "subscription",
        name: producer?.name ?? "Unknown Producer",
        status: "pending",
        draftId: sub.draftId,
      };
    });

    return [consumerItem, ...subscriptionItems];
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const [consumerId, setConsumerId] = useState<string | null>(null);
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [waitingForReady, setWaitingForReady] = useState(false);
  const [waitingForSubscriptionReady, setWaitingForSubscriptionReady] = useState<number | null>(null);

  const [, , consumerError, createConsumer] = useCreateConsumer();
  const [, , subscriptionError, createSubscription] = useCreateSubscription();
  const [, , , getConsumer] = useGetConsumer();
  const [, , , getSubscription] = useGetSubscription();

  // Track if provisioning has started
  const provisioningStarted = useRef(false);

  const updateItemStatus = useCallback(
    (index: number, status: ProvisionStatus, errorMessage?: string) => {
      setItems((prev) => {
        const newItems = [...prev];
        newItems[index] = { ...newItems[index], status, errorMessage };
        return newItems;
      });
    },
    []
  );

  // Helper to poll for consumer ready status
  const waitForConsumerReady = useCallback(
    async (consumerIdToCheck: string, maxAttempts = 60): Promise<boolean> => {
      setWaitingForReady(true);
      
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const consumerData = await getConsumer(consumerIdToCheck);
          if (consumerData?.consumer?.isReady) {
            setWaitingForReady(false);
            return true;
          }
        } catch (err) {
          // Continue polling on error
        }
        
        // Wait 1000ms after response before next attempt
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      
      setWaitingForReady(false);
      return false; // Timed out
    },
    [getConsumer]
  );

  // Helper to poll for subscription ready status
  const waitForSubscriptionReady = useCallback(
    async (consumerIdToCheck: string, subscriptionId: string, itemIndex: number, maxAttempts = 60): Promise<boolean> => {
      setWaitingForSubscriptionReady(itemIndex);
      
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        try {
          const subscriptionData = await getSubscription(consumerIdToCheck, subscriptionId);
          if (subscriptionData?.subscription?.isReady) {
            setWaitingForSubscriptionReady(null);
            return true;
          }
        } catch (err) {
          // Continue polling on error
        }
        
        // Wait 1000ms after response before next attempt
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }
      
      setWaitingForSubscriptionReady(null);
      return false; // Timed out
    },
    [getSubscription]
  );

  const provisionConsumer = useCallback(async (): Promise<string | null> => {
    updateItemStatus(0, "loading");

    try {
      const result = await createConsumer({
        maintainerIds: consumer.maintainerIds,
        description: consumer.description,
        serviceGPRId: consumer.serviceGPRId,
        status: consumer.status,
      });

      if (result?.error || !result?.consumer?.id) {
        const errorMsg = result?.error?.message ?? "Failed to create consumer";
        updateItemStatus(0, "error", errorMsg);
        setHasError(true);
        return null;
      }

      const newConsumerId = result.consumer.id;
      setConsumerId(newConsumerId);
      onConsumerCreated?.(newConsumerId);

      // Wait for consumer to be ready before marking as success
      const isReady = await waitForConsumerReady(newConsumerId);
      
      if (!isReady) {
        updateItemStatus(0, "error", "Consumer creation timed out waiting for ready state");
        setHasError(true);
        return null;
      }

      updateItemStatus(0, "success");
      return newConsumerId;
    } catch (err: any) {
      const errorMsg = err?.message ?? "Failed to create consumer";
      updateItemStatus(0, "error", errorMsg);
      setHasError(true);
      return null;
    }
  }, [consumer, createConsumer, updateItemStatus, onConsumerCreated, waitForConsumerReady]);

  const provisionSubscription = useCallback(
    async (index: number, consumerIdToUse: string): Promise<boolean> => {
      const subscriptionIndex = index - 1; // Account for consumer being at index 0
      const subscription = subscriptions[subscriptionIndex];

      if (!subscription) return false;

      updateItemStatus(index, "loading");

      try {
        const request: ISubscriptionCreateRequest = {
          producerId: subscription.producerId,
          status: subscription.status,
          accountIdFilter: subscription.accountIdFilter,
          iTwinIdFilter: subscription.iTwinIdFilter,
          resourceDataCenterIdFilter: subscription.resourceDataCenterIdFilter,
          iTwinDataCenterIdFilter: subscription.iTwinDataCenterIdFilter,
          // If useAllEvents, send empty array for "no filter"
          events: subscription.useAllEvents ? [] : subscription.events,
          // Explicitly indicate future events preference
          subscribesToFutureEvents: subscription.useAllEvents,
        };

        const result = await createSubscription(consumerIdToUse, request);

        if (result?.error) {
          const errorMsg = result.error.message ?? "Failed to create subscription";
          updateItemStatus(index, "error", errorMsg);
          setHasError(true);
          return false;
        }

        // Wait for subscription to be ready
        if (result?.subscription?.id) {
          const isReady = await waitForSubscriptionReady(consumerIdToUse, result.subscription.id, index);
          if (!isReady) {
            const errorMsg = "Subscription creation timed out waiting for ready status";
            updateItemStatus(index, "error", errorMsg);
            setHasError(true);
            return false;
          }
        }

        updateItemStatus(index, "success");
        return true;
      } catch (err: any) {
        const errorMsg = err?.message ?? "Failed to create subscription";
        updateItemStatus(index, "error", errorMsg);
        setHasError(true);
        return false;
      }
    },
    [subscriptions, createSubscription, updateItemStatus, waitForSubscriptionReady]
  );

  const startProvisioning = useCallback(async () => {
    if (provisioningStarted.current) return;
    provisioningStarted.current = true;
    setIsProvisioning(true);
    setHasError(false);

    // Step 1: Create consumer
    const newConsumerId = await provisionConsumer();
    if (!newConsumerId) {
      setIsProvisioning(false);
      return;
    }

    // Step 2: Create subscriptions sequentially
    for (let i = 1; i < items.length; i++) {
      setCurrentIndex(i);
      const success = await provisionSubscription(i, newConsumerId);
      if (!success) {
        setIsProvisioning(false);
        return;
      }
    }

    // All done!
    setIsProvisioning(false);
    setTimeout(() => {
      onComplete(newConsumerId);
    }, 1000); // Brief delay to show success state
  }, [items.length, provisionConsumer, provisionSubscription, onComplete]);

  // Auto-start provisioning on mount
  useEffect(() => {
    startProvisioning();
  }, [startProvisioning]);

  const handleRetry = useCallback(async () => {
    if (!consumerId && currentIndex === 0) {
      // Retry consumer creation
      provisioningStarted.current = false;
      startProvisioning();
      return;
    }

    if (consumerId && currentIndex > 0) {
      // Retry from current subscription
      setIsProvisioning(true);
      setHasError(false);

      for (let i = currentIndex; i < items.length; i++) {
        // Only retry items that are pending or errored
        if (items[i].status === "error" || items[i].status === "pending") {
          setCurrentIndex(i);
          const success = await provisionSubscription(i, consumerId);
          if (!success) {
            setIsProvisioning(false);
            return;
          }
        }
      }

      setIsProvisioning(false);
      setTimeout(() => {
        onComplete(consumerId);
      }, 1000);
    }
  }, [consumerId, currentIndex, items, provisionSubscription, onComplete, startProvisioning]);

  const StatusIcon = ({ status }: { status: ProvisionStatus }) => {
    switch (status) {
      case "loading":
        return <ProgressRadial size="small" indeterminate />;
      case "success":
        return (
          <SvgStatusSuccess
            style={{
              fill: "var(--iui-color-icon-positive)",
              width: 24,
              height: 24,
            }}
          />
        );
      case "error":
        return (
          <SvgStatusError
            style={{
              fill: "var(--iui-color-icon-negative)",
              width: 24,
              height: 24,
            }}
          />
        );
      default:
        return (
          <div
            style={{
              width: 24,
              height: 24,
              borderRadius: "50%",
              border: "2px solid var(--iui-color-border)",
            }}
          />
        );
    }
  };

  return (
    <div style={{ padding: "24px" }}>
      <Text variant="headline" as="h2" style={{ marginBottom: "8px" }}>
        {hasError ? "Provisioning Stopped" : isProvisioning ? "Provisioning Resources..." : "Provisioning Complete"}
      </Text>
      <Text isMuted style={{ marginBottom: "24px" }}>
        {hasError
          ? "An error occurred during provisioning. You can retry to continue."
          : isProvisioning
          ? "Please wait while we set up your consumer and subscriptions."
          : "All resources have been created successfully."}
      </Text>

      <div style={{ maxWidth: "600px" }}>
        {items.map((item, index) => (
          <Surface
            key={`${item.type}-${item.draftId ?? index}`}
            elevation={1}
            style={{
              padding: "16px",
              marginBottom: "12px",
              borderLeft:
                item.status === "error"
                  ? "3px solid var(--iui-color-border-negative)"
                  : item.status === "success"
                  ? "3px solid var(--iui-color-border-positive)"
                  : undefined,
            }}
          >
            <Flex gap="m" alignItems="center">
              <StatusIcon status={item.status} />
              <div style={{ flex: 1 }}>
                <Text variant="leading" style={{ fontWeight: 500 }}>
                  {item.type === "consumer" ? "Consumer" : "Subscription"}
                </Text>
                <Text isMuted>{item.name}</Text>
                {item.status === "error" && item.errorMessage && (
                  <Text
                    style={{
                      color: "var(--iui-color-text-negative)",
                      fontSize: "0.9em",
                      marginTop: "4px",
                    }}
                  >
                    {item.errorMessage}
                  </Text>
                )}
              </div>
              {item.status === "loading" && (
                <Text isMuted style={{ fontSize: "0.85em" }}>
                  {item.type === "consumer" && waitingForReady
                    ? "Waiting for ready..."
                    : item.type === "subscription" && waitingForSubscriptionReady === index
                    ? "Waiting for ready..."
                    : "Creating..."}
                </Text>
              )}
            </Flex>
          </Surface>
        ))}
      </div>

      {hasError && (
        <Flex gap="s" style={{ marginTop: "16px" }}>
          <Button onClick={handleRetry}>Retry</Button>
          {onBack && (
            <Button styleType="default" onClick={onBack}>
              {consumerId ? "Cancel" : "Back to Edit"}
            </Button>
          )}
        </Flex>
      )}
    </div>
  );
};
